import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, ExternalLink } from "lucide-react";
import { AppHeader } from "@/components/brand/AppHeader";
import { MobileMenu } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { listGroups, createGroup, updateGroup, listPresets, savePreset, deletePreset, errText } from "@/lib/bidCenterApi";

const EMPTY = { name: "", url: "", disciplines: [], country: "", region: "", rules: "", eligibility: "profile", membership: "member", active: true, notes: "" };
const ELIG = { profile: "Personal profile", page: "Business Page", both: "Profile or Page" };
const MEMB = { member: "Member", pending: "Request pending", not_member: "Not a member" };

function GroupForm({ g, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...g, disciplines: (g?.disciplines || []).join(", ") });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { ...f, disciplines: f.disciplines.split(",").map((x) => x.trim()).filter(Boolean) };
    delete body.id; delete body.history;
    try { g?.id ? await updateGroup(g.id, body) : await createGroup(body); toast.success("Group saved."); onSaved(); } catch (e) { toast.error(errText(e)); }
  };
  const sel = "h-11 sm:h-9 w-full rounded-md border px-2 text-base sm:text-sm bg-white";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="group-form">
        <DialogHeader><DialogTitle>{g?.id ? "Edit group" : "Add group"}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <Input data-testid="group-name" value={f.name} onChange={set("name")} placeholder="Group name" className="h-11 sm:h-9" />
          <Input data-testid="group-url" value={f.url} onChange={set("url")} placeholder="https://www.facebook.com/groups/…" className="h-11 sm:h-9" />
          <Input data-testid="group-disciplines" value={f.disciplines} onChange={set("disciplines")} placeholder="Disciplines, comma separated (barrels, ranch…)" className="h-11 sm:h-9" />
          <div className="grid grid-cols-2 gap-2">
            <Input data-testid="group-country" value={f.country || ""} onChange={set("country")} placeholder="Country" className="h-11 sm:h-9" />
            <Input data-testid="group-region" value={f.region || ""} onChange={set("region")} placeholder="Province / state" className="h-11 sm:h-9" />
            <select data-testid="group-eligibility" value={f.eligibility} onChange={set("eligibility")} className={sel}>{Object.entries(ELIG).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            <select data-testid="group-membership" value={f.membership} onChange={set("membership")} className={sel}>{Object.entries(MEMB).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          </div>
          <Textarea data-testid="group-rules" value={f.rules || ""} onChange={set("rules")} placeholder="Posting rules and restrictions" />
          <Textarea data-testid="group-notes" value={f.notes || ""} onChange={set("notes")} placeholder="Notes" />
          <label className="flex items-center gap-2"><Switch data-testid="group-active" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} />Active</label>
          <Button data-testid="group-save" onClick={save} className="w-full h-11 bg-amber-800 hover:bg-amber-900 text-white">Save group</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Presets({ groups, presets, reload }) {
  const [name, setName] = useState("");
  const [pick, setPick] = useState([]);
  const add = async () => {
    try { await savePreset({ name, group_ids: pick }); setName(""); setPick([]); toast.success("Preset saved."); reload(); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <section className="rounded-xl border bg-white p-4 space-y-2" data-testid="group-presets">
      <p className="font-semibold">Presets</p>
      {presets.map((p) => (
        <div key={p.id} className="flex justify-between text-sm" data-testid={`group-preset-${p.id}`}>
          <span>{p.name} · {p.group_ids.length} group{p.group_ids.length === 1 ? "" : "s"}</span>
          <button className="text-xs text-red-700" data-testid={`group-preset-delete-${p.id}`} onClick={() => deletePreset(p.id).then(reload)}>Delete</button>
        </div>
      ))}
      <Input data-testid="group-preset-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="New preset name" className="h-11 sm:h-9" />
      <div className="flex flex-wrap gap-2">{groups.filter((g) => g.active).map((g) => (
        <label key={g.id} className="text-xs flex items-center gap-1 rounded-full border px-2 py-1">
          <input type="checkbox" data-testid={`group-preset-pick-${g.id}`} checked={pick.includes(g.id)} onChange={(e) => setPick(e.target.checked ? [...pick, g.id] : pick.filter((x) => x !== g.id))} />{g.name}
        </label>))}</div>
      <Button variant="outline" data-testid="group-preset-save" onClick={add} className="h-11 sm:h-9">Save preset</Button>
    </section>
  );
}

export default function GroupLibrary() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [presets, setPresets] = useState([]);
  const [edit, setEdit] = useState(null);
  const reload = () => { listGroups().then(setGroups).catch((e) => toast.error(errText(e))); listPresets().then(setPresets).catch(() => {}); };
  useEffect(() => { reload(); }, []);
  return (
    <div className="min-h-screen bg-stone-50">
      <AppHeader title="Facebook Group Library" onBack={() => navigate("/")} backTestId="groups-back" width="max-w-5xl"><MobileMenu /></AppHeader>
      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-4 space-y-4" data-testid="group-library">
        <div className="flex justify-between items-center gap-2">
          <p className="text-sm text-stone-600">Groups you share auctions into. Facebook has no group posting API: every share is confirmed by a person.</p>
          <Button variant="outline" data-testid="group-overview-link" onClick={() => navigate("/groups/overview")} className="h-11 sm:h-9 shrink-0">Overview</Button>
          <Button data-testid="group-add" onClick={() => setEdit({})} className="h-11 sm:h-9 shrink-0 bg-amber-800 hover:bg-amber-900 text-white"><Plus className="h-4 w-4 mr-1" />Add group</Button>
        </div>
        {!groups.length && <p className="text-sm text-stone-500" data-testid="group-empty">No groups yet.</p>}
        {groups.map((g) => (
          <div key={g.id} data-testid={`group-${g.id}`} className={`rounded-xl border bg-white p-4 space-y-1 ${g.active ? "" : "opacity-60"}`}>
            <div className="flex justify-between gap-2">
              <p className="font-semibold">{g.name} {!g.active && <span className="text-xs text-stone-500">(inactive)</span>}</p>
              <button data-testid={`group-edit-${g.id}`} onClick={() => setEdit(g)} className="text-stone-500"><Pencil className="h-4 w-4" /></button>
            </div>
            <a href={g.url} target="_blank" rel="noreferrer" className="text-xs text-[#1877F2] inline-flex items-center gap-1 break-all"><ExternalLink className="h-3 w-3" />{g.url}</a>
            <p className="text-xs text-stone-600">{[g.region, g.country].filter(Boolean).join(", ") || "No location"} · {(g.disciplines || []).join(", ") || "no disciplines"} · {ELIG[g.eligibility]} · {MEMB[g.membership]}</p>
            {g.rules && <p className="text-xs"><b>Rules:</b> {g.rules}</p>}
            {g.notes && <p className="text-xs text-stone-500">{g.notes}</p>}
            {g.history.length > 0 && <details className="text-xs"><summary className="cursor-pointer text-stone-500">Posting history ({g.history.length})</summary>
              {g.history.map((h, i) => <p key={i}>{h.horse_name} · {h.status}{h.posted_url ? ` · ${h.posted_url}` : ""}</p>)}</details>}
          </div>
        ))}
        <Presets groups={groups} presets={presets} reload={reload} />
        {edit && <GroupForm g={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
      </main>
    </div>
  );
}
